import React from "react";
import DOMPurify from "dompurify";
import "./blog-content.css";

const safeUrl = (value) =>
  /^(https?:\/\/|mailto:|tel:|\/(?!\/)|#)/i.test(value || "") ? value : "";
// Live custom HTML is sanitized before it ever reaches the editor DOM, too.
export const safeHtml = (html) => {
  const fragment = DOMPurify.sanitize(html || "", {
    RETURN_DOM_FRAGMENT: true,
    FORBID_TAGS: ["style", "iframe", "form", "input", "button", "svg", "math"],
    FORBID_ATTR: ["id", "name"],
  });
  const properties = new Set([
    "color",
    "background-color",
    "text-align",
    "font-size",
    "font-weight",
    "font-family",
    "line-height",
    "padding",
    "margin",
    "border-radius",
    "width",
    "max-width",
  ]);
  fragment.querySelectorAll("[style]").forEach((element) => {
    Array.from(element.style).forEach((property) => {
      const value = element.style.getPropertyValue(property);
      if (
        !properties.has(property) ||
        /url\s*\(|expression|var\s*\(|[<>]/i.test(value)
      )
        element.style.removeProperty(property);
    });
  });
  const wrapper = document.createElement("div");
  wrapper.appendChild(fragment);
  return wrapper.innerHTML;
};
const cssLength = (value) =>
  typeof value === "number" ? `${value}px` : value || "auto";
export const blockStyle = (block) => {
  const desktop = block.style || {},
    mobile = block.mobileStyle || {};
  const media = ["image", "video", "spacer"].includes(block.type);
  const style = { ...desktop };
  if (media) delete style.height;
  if (block.type === "container") {
    delete style.height;
    style.minHeight = desktop.height || desktop.minHeight;
  }
  style["--blog-auto-padding"] = `${Math.min(
    24,
    Number(desktop.padding) || 0,
  )}px`;
  style["--blog-flex"] =
    desktop.width && desktop.width !== "auto" ? "0 1 auto" : "1 1 0%";
  style["--blog-media-height"] = cssLength(desktop.height);
  style["--blog-fit"] = desktop.objectFit || "cover";
  // Reset these on every block: mobile dimensions must not leak into descendants.
  style["--blog-mobile-width"] = cssLength(mobile.width || "100%");
  style["--blog-mobile-height"] = cssLength(mobile.height);
  style["--blog-mobile-min-height"] = cssLength(
    mobile.minHeight || (block.type === "container" ? mobile.height : "auto"),
  );
  style["--blog-mobile-flex"] =
    mobile.width && mobile.width !== "auto" ? "0 1 auto" : "1 1 0%";
  style["--blog-mobile-flex-direction"] = mobile.flexDirection || "column";
  style["--blog-mobile-gap"] = `${mobile.gap ?? 16}px`;
  for (const [key, value] of Object.entries(mobile))
    style[
      `--blog-mobile-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`
    ] =
      typeof value === "number" && !["fontWeight", "lineHeight"].includes(key)
        ? `${value}px`
        : value;
  return style;
};
export function BlockContent({ block, children, editing = false }) {
  switch (block.type) {
    case "container":
      return (
        <div
          className="blog-container"
          style={{
            flexDirection: block.style?.flexDirection || "column",
            gap: block.style?.gap ?? 20,
          }}
        >
          {children}
        </div>
      );
    case "heading": {
      const Tag = `h${
        [2, 3, 4].includes(Number(block.level)) ? block.level : 2
      }`;
      return <Tag>{block.text || "Your heading"}</Tag>;
    }
    case "text":
      return <p className="blog-text">{block.text}</p>;
    case "quote":
      return <blockquote>{block.text}</blockquote>;
    case "image":
      if (!block.src && !editing) return null;
      return (
        <figure>
          {block.src ? (
            <img
              src={safeUrl(block.src)}
              alt={block.alt || ""}
              loading="lazy"
              style={{
                borderRadius: "inherit",
                objectFit: block.style?.objectFit || "cover",
                height: "var(--blog-media-height, auto)",
              }}
            />
          ) : (
            <div className="blog-image-placeholder">Add an image</div>
          )}
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    case "video":
      if (!block.src && !editing) return null;
      return (
        <figure>
          {block.src ? (
            <video
              src={safeUrl(block.src)}
              poster={safeUrl(block.poster) || undefined}
              title={block.title || "Blog video"}
              controls={editing || block.controls !== false}
              autoPlay={!editing && block.autoplay === true}
              muted={block.autoplay === true || block.muted === true}
              loop={block.loop === true}
              playsInline
              preload={block.preload || "metadata"}
              style={{
                height: "var(--blog-media-height, auto)",
                objectFit: block.style?.objectFit || "cover",
              }}
            />
          ) : (
            <div className="blog-image-placeholder">Add a video</div>
          )}
          {block.caption && <figcaption>{block.caption}</figcaption>}
        </figure>
      );
    case "html":
      return (
        <div
          className="blog-custom-html"
          dangerouslySetInnerHTML={{ __html: safeHtml(block.html) }}
        />
      );
    case "button":
      return (
        <a className="blog-content-button" href={safeUrl(block.href)}>
          {block.text || "Learn more"}
        </a>
      );
    case "divider":
      return <hr />;
    case "spacer":
      return (
        <div
          aria-hidden="true"
          style={{ height: "var(--blog-media-height, 40px)" }}
        />
      );
    default:
      return null;
  }
}
export default function BlogRenderer({ blocks = [], mobile = false }) {
  return (
    <div className={`blog-content ${mobile ? "blog-mobile-preview" : ""}`}>
      {blocks.map((block) => (
        <div
          key={block.id}
          className={`blog-block blog-block-${block.type}`}
          style={blockStyle(block)}
        >
          <BlockContent block={block}>
            {block.children && (
              <BlogRenderer blocks={block.children} mobile={mobile} />
            )}
          </BlockContent>
        </div>
      ))}
    </div>
  );
}
